import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/Function';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [services, setServices] = useState([]);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/service.php/get`);
        const data = await response.json();

        if (data.success) {
          setServices(data.services);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  // Handle scroll for navbar transformation
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  // Prevent body scroll when offcanvas is open
  useEffect(() => {
    if (showOffcanvas) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showOffcanvas]);

  const toggleOffcanvas = () => {
    setShowOffcanvas(!showOffcanvas);
  };

  const closeOffcanvas = () => {
    setShowOffcanvas(false);
    setShowServicesDropdown(false);
  };

  const toggleServicesDropdown = () => {
    setShowServicesDropdown(!showServicesDropdown);
  };

  // Handle service navigation
  const handleServiceNavigation = (serviceSlug) => {
    closeOffcanvas();
    setShowServicesDropdown(false);
    navigate(`/services/${serviceSlug}`);
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeOffcanvas();
        setShowServicesDropdown(false);
      }
    };

    if (showOffcanvas || showServicesDropdown) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showOffcanvas, showServicesDropdown]);

  // Navigation function
  const handleNavigation = (path) => {
    closeOffcanvas();

    // If we're already on the main page and navigating to home, scroll to top
    if (path === '/' && window.location.pathname === '/') {
      scrollToSection('home');
    } else {
      navigate(path);
    }
  };

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    closeOffcanvas();

    if (sectionId === 'home') {
      // Scroll to top for home
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - (isScrolled ? 70 : 100); // Adjust for navbar height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { name: 'Home', id: 'home', type: 'scroll' },
    { name: 'About Us', id: 'about-us', type: 'page', path: '/about-safproview' },
    { name: 'Services', id: 'services', type: 'scroll' },
    { name: 'Why Choose Us', id: 'why-choose-us', type: 'scroll' },
    { name: 'Products', id: 'products', type: 'page', path: '/Productsview' },
    { name: 'Blogs', id: 'blogs', type: 'page', path: '/blogs' },
    { name: 'Contact Us', id: 'contact-us', type: 'page', path: '/Contactus' }
  ];

  const handleNavClick = (item) => {
    if (item.type === 'page') {
      handleNavigation(item.path);
    } else {
      // For scroll items, check if we're on the main page
      if (window.location.pathname === '/') {
        // We're already on main page, just scroll
        scrollToSection(item.id);
      } else {
        // We're on a different page, navigate to main page with section info in state
        navigate('/', { state: { scrollTo: item.id } });
      }
    }
  };

  return (
    <>
      <nav className={`main-navbar ${isScrolled ? 'scrolled' : ''}`} style={{
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(15px)',
        WebkitBackdropFilter: 'blur(15px)',
        padding: isScrolled ? '8px 0' : '12px 0',
        position: 'sticky',
        top: '45px',
        left: 0,
        right: 0,
        zIndex: 1030,
        boxShadow: isScrolled ? '0 10px 40px rgba(0, 0, 0, 0.08)' : '0 4px 30px rgba(0, 0, 0, 0.03)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
        transition: 'all 0.5s cubic-bezier(0.19, 1, 0.22, 1)'
      }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            {/* Logo */}
            <div className="logo">
              <a
                style={{ display: 'block', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick({ id: 'home', type: 'scroll' });
                  navigate('/');
                }}
              >
                <img
                  src="/images/Safpro-logo.png"
                  alt="Logo"
                  style={{
                    width: isScrolled ? '165px' : '190px',
                    height: 'auto',
                    transition: 'all 0.4s ease'
                  }}
                />
              </a>
            </div>

            {/* Desktop Navigation Links - Hidden on mobile */}
            <div className="d-none d-lg-block">
              <ul className="navbar-nav d-flex flex-row mb-0 align-items-center">
                {navItems.map((item) => (
                  item.name === 'Services' ? (
                    // Services Dropdown
                    <li
                      key={item.id}
                      className="nav-item mx-2 position-relative"
                      onMouseEnter={() => setShowServicesDropdown(true)}
                      onMouseLeave={() => setShowServicesDropdown(false)}
                    >
                      <button
                        className="nav-link fw-semibold border-0 bg-transparent d-flex align-items-center premium-nav-link"
                        style={{
                          color: '#24246E',
                          fontSize: '15px',
                          padding: '10px 15px',
                          cursor: 'pointer',
                          fontFamily: 'Poppins, sans-serif',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={toggleServicesDropdown}
                      >
                        Services
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '10px',
                          display: 'inline-block',
                          transition: 'transform 0.4s ease',
                          transform: showServicesDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                          ▼
                        </span>
                      </button>

                      {/* Services Dropdown Menu */}
                      <div
                        className={`dropdown-menu ${showServicesDropdown ? 'show' : ''}`}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(20px)',
                          minWidth: '260px',
                          boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
                          border: '1px solid rgba(255, 255, 255, 0.6)',
                          borderRadius: '16px',
                          padding: '12px',
                          zIndex: 1000,
                          visibility: showServicesDropdown ? 'visible' : 'hidden',
                          opacity: showServicesDropdown ? 1 : 0,
                          transform: showServicesDropdown ? 'translateY(10px) scale(1)' : 'translateY(20px) scale(0.95)',
                          transition: 'all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)'
                        }}
                      >
                        {services.length > 0 ? (
                          services.map((service) => (
                            <button
                              key={service.id}
                              className="dropdown-item"
                              style={{
                                padding: '12px 18px',
                                color: '#24246E',
                                fontSize: '14px',
                                fontWeight: '500',
                                borderRadius: '10px',
                                textDecoration: 'none',
                                border: 'none',
                                backgroundColor: 'transparent',
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => handleServiceNavigation(service.slug)}
                            >
                              {service.title}
                            </button>
                          ))
                        ) : (
                          <div
                            className="dropdown-item"
                            style={{
                              padding: '10px 20px',
                              color: '#666',
                              fontSize: '14px',
                              fontStyle: 'italic'
                            }}
                          >
                            Loading services...
                          </div>
                        )}
                      </div>
                    </li>
                  ) : (
                    // Regular navigation items
                    <li key={item.id} className="nav-item mx-2">
                      <button
                        className="nav-link fw-semibold border-0 bg-transparent premium-nav-link"
                        style={{
                          color: '#24246E',
                          fontSize: '15px',
                          padding: '10px 15px',
                          fontFamily: 'Poppins, sans-serif',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleNavClick(item)}
                      >
                        {item.name}
                      </button>
                    </li>
                  )
                ))}
              </ul>
            </div>

            {/* Mobile Toggle Button */}
            <div className="d-lg-none">
              <button
                className={`menu-toggle ${showOffcanvas ? 'active' : ''}`}
                onClick={toggleOffcanvas}
                aria-label="Toggle navigation"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div style={{ height: '0px' }} />

      {/* Offcanvas for Mobile */}
      <div
        className={`offcanvas-mobile ${showOffcanvas ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          boxShadow: '-10px 0 40px rgba(0,0,0,0.1)',
          transform: showOffcanvas ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.5s cubic-bezier(0.77, 0, 0.175, 1)',
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className="offcanvas-header" style={{
          padding: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(to right, #f8f9fa, #ffffff)'
        }}>
          <img
            src="/images/Safpro-logo.png"
            alt="Logo"
            style={{ width: '150px', height: 'auto' }}
          />
          <button
            className="close-btn"
            onClick={closeOffcanvas}
            style={{
              border: 'none',
              background: '#f0f2f5',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              fontSize: '24px',
              color: '#24246E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            ×
          </button>
        </div>

        <div className="offcanvas-body" style={{
          padding: '20px 0',
          flex: 1,
          overflowY: 'auto'
        }}>
          <ul className="mobile-nav" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {navItems.map((item, index) => (
              <li
                key={item.id}
                style={{
                  opacity: showOffcanvas ? 1 : 0,
                  transform: showOffcanvas ? 'translateX(0)' : 'translateX(20px)',
                  transition: `all 0.4s ease ${index * 0.1}s`
                }}
              >
                {item.name === 'Services' ? (
                  <>
                    <button
                      className="mobile-nav-link d-flex justify-content-between align-items-center"
                      onClick={toggleServicesDropdown}
                    >
                      {item.name}
                      <span style={{
                        fontSize: '12px',
                        transition: 'transform 0.3s ease',
                        transform: showServicesDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}>▼</span>
                    </button>
                    <div style={{
                      maxHeight: showServicesDropdown ? '1000px' : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.4s ease-in-out',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {services.map((service) => (
                        <button
                          key={service.id}
                          className="mobile-sub-link"
                          onClick={() => handleServiceNavigation(service.slug)}
                        >
                          {service.title}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <button
                    className="mobile-nav-link"
                    onClick={() => handleNavClick(item)}
                  >
                    {item.name}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="offcanvas-footer" style={{
          padding: '30px',
          borderTop: '1px solid #f0f2f5',
          textAlign: 'center'
        }}>
          <div className="cta-button-mobile" style={{
            background: '#24246E',
            color: '#fff',
            padding: '15px',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }} onClick={() => handleNavigation('/Contactus')}>
            Get Started
          </div>
          <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
            &copy; {new Date().getFullYear()} Safpro. All rights reserved.
          </p>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className={`offcanvas-backdrop ${showOffcanvas ? 'show' : ''}`}
        onClick={closeOffcanvas}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        
        * {
          font-family: 'Poppins', sans-serif;
        }

        .premium-nav-link {
          position: relative;
          color: #24246E !important;
          z-index: 1;
        }

        .premium-nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: #24246E;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateX(-50%);
        }

        .premium-nav-link:hover::after {
          width: calc(100% - 30px);
        }

        .premium-nav-link:hover {
          color: #24246E !important;
          opacity: 0.8;
        }

        .dropdown-item:hover {
          background-color: #f0f3ff !important;
          color: #24246E !important;
          transform: translateX(5px);
        }

        /* Mobile Menu Toggle Animation */
        .menu-toggle {
          width: 30px;
          height: 20px;
          position: relative;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          z-index: 1100;
        }

        .menu-toggle span {
          display: block;
          position: absolute;
          height: 2px;
          width: 100%;
          background: #24246E;
          border-radius: 9px;
          opacity: 1;
          left: 0;
          transform: rotate(0deg);
          transition: .25s ease-in-out;
        }

        .menu-toggle span:nth-child(1) { top: 0px; }
        .menu-toggle span:nth-child(2) { top: 9px; }
        .menu-toggle span:nth-child(3) { top: 18px; }

        .menu-toggle.active span:nth-child(1) {
          top: 9px;
          transform: rotate(135deg);
        }

        .menu-toggle.active span:nth-child(2) {
          opacity: 0;
          left: -40px;
        }

        .menu-toggle.active span:nth-child(3) {
          top: 9px;
          transform: rotate(-135deg);
        }

        /* Mobile Nav Links */
        .mobile-nav-link {
          width: 100%;
          text-align: left;
          padding: 18px 30px;
          font-size: 17px;
          font-weight: 600;
          color: #24246E;
          background: transparent;
          border: none;
          border-bottom: 1px solid #f0f2f5;
          transition: all 0.3s ease;
        }

        .mobile-nav-link:active {
          background-color: #f8f9fa;
        }

        .mobile-sub-link {
          width: 100%;
          text-align: left;
          padding: 12px 30px 12px 50px;
          font-size: 15px;
          color: #666;
          background: transparent;
          border: none;
          transition: all 0.3s ease;
        }

        /* Backdrop */
        .offcanvas-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(36, 36, 110, 0.4);
          backdrop-filter: blur(4px);
          z-index: 1040;
          opacity: 0;
          visibility: hidden;
          transition: all 0.4s ease;
        }

        .offcanvas-backdrop.show {
          opacity: 1;
          visibility: visible;
        }

        .close-btn:hover {
          background-color: #e2e5e9 !important;
          transform: rotate(90deg);
        }

        @media (max-width: 991px) {
          .main-navbar {
            top: 38px !important; /* Sync with Topnavbar mobile height */
            padding: 8px 0 !important;
          }
          .navbar-logo-img {
            width: 150px !important;
          }
        }

        @media (max-width: 575px) {
          .main-navbar {
            top: 35px !important; /* Sync with Topnavbar small mobile height */
            padding: 5px 0 !important;
          }
          .navbar-logo-img {
            width: 130px !important;
          }
          .menu-toggle {
            width: 26px;
          }
        }
      `}</style>
    </>
  );
}
